import { inject, Injectable } from '@angular/core';
import { AssetService } from '../https-services';
import { map, Observable } from 'rxjs';
import {
	ConfigurationDocumentType,
	DestinationConfigurationsDto,
	DestinationItem,
	DestinationMenuConfigurationDto,
	DestinationMenuItem,
	HeaderMenuConfigurationDto,
	MetaRouteConfiguration,
	PageConfigurationDto,
	TopSlider,
	Widget,
} from '../model-dto';

@Injectable({ providedIn: 'root' })
export class ConfigurationService {
	private readonly assetService = inject(AssetService);

	public getTopSlider(language: string): Observable<TopSlider[]> {
		return this.assetService
			.getConfiguration(language, ConfigurationDocumentType.TopSlider)
			.pipe(map((x) => this.parseJson<TopSlider[]>(x?.json) ?? []));
	}

	public getHomeConfiguration(language: string): Observable<Widget[]> {
		return this.assetService
			.getConfiguration(language, ConfigurationDocumentType.Home)
			.pipe(map((x) => this.parseJson<Widget[]>(x?.json) ?? []));
	}

	public getDestinations(language: string): Observable<DestinationItem[]> {
		return this.assetService.getConfiguration(language, ConfigurationDocumentType.Destinations).pipe(
			map((destinations) => {
				const response = this.parseJson<DestinationConfigurationsDto>(destinations.json);
				if (!response) {
					return [];
				}
				return Object.entries(response).reduce((acc, [key, value]) => {
					acc.push({
						...value,
						key,
					});
					return acc;
				}, [] as DestinationItem[]);
			})
		);
	}

	public getDestinationMenu(language: string): Observable<DestinationMenuItem[]> {
		return this.assetService.getConfiguration(language, ConfigurationDocumentType.DestinationMenu).pipe(
			map((response) => {
				const parsed = this.parseJson<DestinationMenuConfigurationDto>(response?.json);
				if (!parsed) {
					return [];
				}
				return Object.entries(parsed).reduce((acc, [key, value]) => {
					acc.push({ ...value, key });
					return acc;
				}, [] as DestinationMenuItem[]);
			})
		);
	}

	public getHeaderMenu(language: string): Observable<HeaderMenuConfigurationDto> {
		return this.assetService
			.getConfiguration(language, ConfigurationDocumentType.HeaderMenu)
			.pipe(map((response) => this.parseJson<HeaderMenuConfigurationDto>(response?.json) ?? {}));
	}

	public getPageData(
		language: string,
		type:
			| ConfigurationDocumentType.Premium
			| ConfigurationDocumentType.SpecialOffer
			| ConfigurationDocumentType.LastMinute
			| ConfigurationDocumentType.FirstMinute
	): Observable<PageConfigurationDto> {
		return this.assetService.getConfiguration(language, type).pipe(
			map(
				(x) =>
					this.parseJson<PageConfigurationDto>(x?.json) ?? {
						description: '',
						title: '',
						widgets: [],
						topSliderImages: [],
						visible: false,
					}
			)
		);
	}

	public getMetaData(language: string): Observable<MetaRouteConfiguration[]> {
		return this.assetService.getConfiguration(language, ConfigurationDocumentType.Meta).pipe(
			map((meta) => {
				const response = this.parseJson<MetaRouteConfiguration>(meta.json);
				if (!response) {
					return [];
				}
				return Object.entries(response).reduce((acc, [key, value]) => {
					acc.push({
						...value,
						key,
					});
					return acc;
				}, [] as MetaRouteConfiguration[]);
			})
		);
	}

	private parseJson<T>(json?: string): T | null {
		if (!json) {
			return null;
		}
		return JSON.parse(json);
	}
}
